import { MoreVert } from '@mui/icons-material';
import { Box, IconButton, Link, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import useUnitProvider from '/@/core/context/unit';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { getUserDisplayName, RegistryFooter } from './card-footer';
import { Cell } from './data-grid';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';

/** Date / Quantity / Estimated yield / per-sample link. */
const COLUMNS = 'repeat(4, minmax(0, 1fr))';

const RegistryYieldEstimation = ({
    cropMonitoring,
    notes,
    onViewResults,
    onAddEstimation,
}: {
    cropMonitoring: CropMonitoring;
    notes: CropMonitoringNote[];
    /** Opens a specific estimation's read-only per-sample results ("Resultado por muestras"). */
    onViewResults?: (estimationId: number) => void;
    /** Opens a blank form to add another estimation to the campaign. */
    onAddEstimation?: () => void;
}) => {
    const units = useUnitProvider();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    // API returns estimations newest-first.
    const estimations = cropMonitoring.cornCropMonitoring?.yieldEstimations ?? [];
    const newest = estimations[0];

    const userNodeQuery = useGetUserNode(newest?.createdBy ?? 0);

    const closeMenu = () => setMenuAnchor(null);
    const handleAdd = () => {
        closeMenu();
        onAddEstimation?.();
    };

    if (estimations.length === 0) return null;

    const authorName = getUserDisplayName(userNodeQuery.data);
    const registeredAt = newest?.created ? formatDate(new Date(newest.created)) : null;

    return (
        <RegistryCard>
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Typography variant='h6' color='primary'>
                    {_('Estimación de rinde')}
                </Typography>
                <IconButton
                    size='small'
                    aria-label={_('Más opciones')}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                >
                    <MoreVert />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                    <MenuItem onClick={handleAdd}>{_('Agregar estimación')}</MenuItem>
                </Menu>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, width: '100%', overflow: 'auto' }}>
                <Cell header>{_('Fecha del muestreo')}</Cell>
                <Cell header>{_('Cantidad')}</Cell>
                <Cell header>{_('Rendimiento estimado')}</Cell>
                <Cell header>{''}</Cell>
                {estimations.map((estimation) => (
                    <Fragment key={estimation.id}>
                        <Cell>{estimation.created ? formatDate(new Date(estimation.created)) : '—'}</Cell>
                        <Cell>{`${estimation.yieldSamples?.length ?? 0} ${_('muestras')}`}</Cell>
                        <Cell>
                            {estimation.result != null
                                ? units.toYield(estimation.result, cropMonitoring.crop, 2).toString()
                                : '—'}
                        </Cell>
                        <Cell>
                            <Link
                                component='button'
                                color='secondary'
                                underline='always'
                                onClick={() => onViewResults?.(estimation.id)}
                            >
                                {_('Resultado por muestras')}
                            </Link>
                        </Cell>
                    </Fragment>
                ))}
            </Box>

            {newest && <RegistryNotes notes={notes} fieldId={cropMonitoring.field} cornYieldEstimationId={newest.id} />}

            {registeredAt && <RegistryFooter authorName={authorName} date={registeredAt} />}
        </RegistryCard>
    );
};

export default RegistryYieldEstimation;
